import React, { useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
} from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const CLOUD_FUNCTIONS_BASE = 'https://us-central1-skilltrade-app-2026.cloudfunctions.net';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [checkingRedirect, setCheckingRedirect] = useState(true);
  const navigate = useNavigate();

  // Handle Safari redirect result on page load
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          navigate('/dashboard');
          return;
        }
      } catch (err) {
        console.error('Redirect result error:', err);
        if (err.code !== 'auth/no-auth-event') {
          setError('Google sign-in failed. Please try again.');
        }
      } finally {
        setCheckingRedirect(false);
      }
    };
    handleRedirectResult();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/dashboard');
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    setGoogleLoading(true);
    try {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isSafari || isMobile) {
        await signInWithRedirect(auth, provider);
      } else {
        await signInWithPopup(auth, provider);
        navigate('/dashboard');
      }
    } catch (err) {
      setError('Google sign-in failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      setResetError('Please enter your email address.');
      return;
    }
    setResetLoading(true);
    setResetError('');
    try {
      const res = await fetch(
        `${CLOUD_FUNCTIONS_BASE}/sendPasswordReset`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: resetEmail.trim() }),
        }
      );
      const data = await res.json();
      if (data.success) {
        setResetSent(true);
      } else {
        setResetError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setResetError('Something went wrong. Please try again.');
    }
    setResetLoading(false);
  };

  // Show loading while checking for redirect result
  if (checkingRedirect) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.logo}>TradePower</h1>
          <p style={{ color: '#666', fontSize: '14px' }}>Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
          TradePower
        </h1>
        <p style={styles.tagline}>Find skilled workers. Find great work.</p>

        {/* Main Login Form */}
        {!showForgot && (
          <>
            {error && <p style={styles.error}>{error}</p>}
            <form onSubmit={handleLogin}>
              <input
                style={styles.input}
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                style={styles.input}
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <div style={styles.forgotRow}>
                <span style={styles.forgotLink} onClick={() => {
                  setShowForgot(true);
                  setResetEmail(email);
                  setError('');
                }}>
                  Forgot Password?
                </span>
              </div>
              <button style={styles.button} type="submit">Log In</button>
            </form>

            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>or</span>
              <div style={styles.dividerLine} />
            </div>

            <button
              style={googleLoading ? styles.googleButtonLoading : styles.googleButton}
              onClick={handleGoogleLogin}
              disabled={googleLoading}>
              {googleLoading ? '⏳ Redirecting...' : '🔵 Sign in with Google'}
            </button>

            <p style={styles.registerText}>
              Don't have an account?{' '}
              <span style={styles.link} onClick={() => navigate('/register')}>
                Sign up
              </span>
            </p>

            <div style={styles.legalLinks}>
              <span style={styles.legalLink} onClick={() => navigate('/terms')}>
                Terms of Service
              </span>
              <span style={styles.legalDivider}>•</span>
              <span style={styles.legalLink} onClick={() => navigate('/privacy')}>
                Privacy Policy
              </span>
            </div>
          </>
        )}

        {/* Forgot Password Form */}
        {showForgot && !resetSent && (
          <>
            <div style={styles.forgotHeader}>
              <p style={styles.forgotTitle}>Reset Your Password</p>
              <p style={styles.forgotSubtitle}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>
            {resetError && <p style={styles.error}>{resetError}</p>}
            <form onSubmit={handlePasswordReset}>
              <input
                style={styles.input}
                type="email"
                placeholder="Email address"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
              <button
                style={resetLoading ? styles.buttonDisabled : styles.button}
                type="submit"
                disabled={resetLoading}>
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            <button style={styles.backLink} onClick={() => {
              setShowForgot(false);
              setResetError('');
              setResetEmail('');
            }}>
              ← Back to Login
            </button>
          </>
        )}

        {/* Reset Email Sent Confirmation */}
        {showForgot && resetSent && (
          <div style={styles.successBox}>
            <p style={styles.successIcon}>📧</p>
            <p style={styles.successTitle}>Check Your Email</p>
            <p style={styles.successText}>
              We sent a password reset link to <strong>{resetEmail}</strong>.
              Check your inbox and click the link to reset your password.
            </p>
            <p style={styles.successNote}>
              Didn't get it? Check your spam folder or{' '}
              <span style={styles.link} onClick={() => {
                setResetSent(false);
                setResetEmail('');
              }}>
                try again
              </span>
            </p>
            <button style={styles.button} onClick={() => {
              setShowForgot(false);
              setResetSent(false);
              setResetEmail('');
            }}>
              Back to Login
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh', backgroundColor: '#f0f2f5',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
  },
  card: {
    backgroundColor: 'white', padding: '40px', borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)', width: '100%',
    maxWidth: '400px', textAlign: 'center',
  },
  logo: { fontSize: '32px', fontWeight: 'bold', color: '#E94560', marginBottom: '8px' },
  tagline: { color: '#666', marginBottom: '24px' },
  input: {
    width: '100%', padding: '12px', marginBottom: '16px',
    borderRadius: '8px', border: '1px solid #ddd',
    fontSize: '16px', boxSizing: 'border-box',
  },
  forgotRow: { textAlign: 'right', marginTop: '-8px', marginBottom: '16px' },
  forgotLink: {
    fontSize: '13px', color: '#E94560', cursor: 'pointer', fontWeight: 'bold',
  },
  button: {
    width: '100%', padding: '12px', backgroundColor: '#E94560',
    color: 'white', border: 'none', borderRadius: '8px',
    fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '12px',
  },
  buttonDisabled: {
    width: '100%', padding: '12px', backgroundColor: '#ccc',
    color: 'white', border: 'none', borderRadius: '8px',
    fontSize: '16px', fontWeight: 'bold', cursor: 'not-allowed', marginBottom: '12px',
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px',
  },
  dividerLine: { flex: 1, height: '1px', backgroundColor: '#eee' },
  dividerText: { fontSize: '13px', color: '#999' },
  googleButton: {
    width: '100%', padding: '12px', backgroundColor: 'white',
    color: '#333', border: '1px solid #ddd', borderRadius: '8px',
    fontSize: '16px', cursor: 'pointer', marginBottom: '20px',
  },
  googleButtonLoading: {
    width: '100%', padding: '12px', backgroundColor: '#f8f9fa',
    color: '#999', border: '1px solid #ddd', borderRadius: '8px',
    fontSize: '16px', cursor: 'not-allowed', marginBottom: '20px',
  },
  error: { color: '#dc3545', marginBottom: '12px', fontSize: '14px' },
  registerText: { color: '#666', marginBottom: '16px' },
  link: { color: '#E94560', cursor: 'pointer', fontWeight: 'bold' },
  legalLinks: {
    display: 'flex', justifyContent: 'center',
    alignItems: 'center', gap: '8px', marginTop: '8px',
  },
  legalLink: {
    fontSize: '12px', color: '#999', cursor: 'pointer', textDecoration: 'underline',
  },
  legalDivider: { fontSize: '12px', color: '#ccc' },
  forgotHeader: { marginBottom: '20px' },
  forgotTitle: { fontSize: '20px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' },
  forgotSubtitle: { fontSize: '14px', color: '#666', lineHeight: '1.5' },
  backLink: {
    background: 'none', border: 'none', color: '#666',
    cursor: 'pointer', fontSize: '14px', marginTop: '8px',
  },
  successBox: { textAlign: 'center' },
  successIcon: { fontSize: '48px', marginBottom: '12px' },
  successTitle: { fontSize: '20px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' },
  successText: { fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '12px' },
  successNote: { fontSize: '13px', color: '#999', marginBottom: '20px' },
};

export default Login;